/**
 * Configuración centralizada para el sistema de autenticación
 */
export const AUTH_CONFIG = {
  // ===== ENDPOINTS =====
  /** Endpoint para realizar login */
  LOGIN_ENDPOINT: '/auth/login',

  /** Endpoint para refrescar tokens */
  REFRESH_ENDPOINT: '/auth/refresh',

  /** Endpoint para obtener información del usuario actual */
  USER_ME_ENDPOINT: '/user/me',

  // ===== STORAGE KEYS =====
  /** Clave de localStorage para el access token */
  ACCESS_TOKEN_KEY: 'access_token',

  /** Clave de localStorage para el refresh token */
  REFRESH_TOKEN_KEY: 'refresh_token',

  /** Clave de localStorage para almacenar información del usuario */
  USER_DATA_KEY: 'authUser',

  // ===== TOKEN REFRESH SETTINGS =====
  /** Habilitar renovación proactiva de tokens antes de que expiren */
  PROACTIVE_REFRESH_ENABLED: true,

  /**
   * Umbral en milisegundos antes de la expiración para renovar proactivamente.
   * Default: 2 minutos (120000 ms)
   */
  PROACTIVE_REFRESH_THRESHOLD_MS: 120000,

  /**
   * Intervalo de verificación del timer en milisegundos.
   * Default: 30 segundos (30000 ms)
   */
  CHECK_INTERVAL_MS: 30000,

  // ===== EXCLUDED ROUTES =====
  /**
   * Rutas que NO deben incluir el header Authorization.
   * El interceptor saltará la inyección del token en estas rutas.
   */
  EXCLUDED_ROUTES: ['/auth/login', '/auth/refresh', 'yulia.data.yurest.dev/v1/conversations', '/stream'],

  // ===== PUBLIC ROUTES =====
  /**
   * Rutas públicas que no requieren autenticación.
   * Útil para guards y navegación.
   */
  PUBLIC_ROUTES: ['/login', '/register', '/forgot-password'],

  // ===== ERROR CODES =====
  /**
   * Códigos de error que devuelve el backend en respuestas 401.
   * Usados para manejar distintos escenarios de fallo de autenticación.
   */
  ERROR_CODES: {
    /** Token expirado - se debe intentar refresh */
    TOKEN_EXPIRED: 'token_expired',

    /** Token inválido - logout y redirigir a login */
    TOKEN_INVALID: 'token_invalid',

    /** No autenticado - falta token o es inválido */
    NOT_AUTHENTICATED: 'not_authenticated',

    /** Error genérico de autenticación */
    AUTHENTICATION_ERROR: 'authentication_error',
  },

  // ===== ERROR MESSAGES =====
  /** Mensajes de error para mostrar al usuario */
  ERROR_MESSAGES: {
    TOKEN_EXPIRED: 'Tu sesión ha expirado. Renovando automáticamente...',
    TOKEN_INVALID: 'Token inválido. Por favor, inicia sesión nuevamente.',
    NOT_AUTHENTICATED: 'No estás autenticado. Redirigiendo al login...',
    AUTHENTICATION_ERROR: 'Error de autenticación. Por favor, intenta de nuevo.',
    REFRESH_FAILED: 'No se pudo renovar tu sesión. Por favor, inicia sesión nuevamente.',
    NO_REFRESH_TOKEN: 'No hay refresh token disponible. Por favor, inicia sesión.',
  },
} as const;

/**
 * Tipo para los códigos de error
 */
export type AuthErrorCode = (typeof AUTH_CONFIG.ERROR_CODES)[keyof typeof AUTH_CONFIG.ERROR_CODES];
